/* =====================================================================
   Search Premium — <premium-search> custom element
   - Debounced predictive search against /search/suggest.json
   - Renders custom section: predictive-search-premium
   - Manages recent-search localStorage, trending chip clicks
   - Mobile/desktop unified — CSS handles layout differences
   ===================================================================== */
(function () {
  'use strict';

  const RECENT_KEY = 'gh:recentSearches';
  const RECENT_MAX = 6;
  const DEBOUNCE_MS = 250;
  const MIN_QUERY = 2;
  const SECTION_ID = 'predictive-search-premium';
  const POPULAR = ['Oud', 'Best Sellers', 'Home Spray', 'Limited Editions'];

  class PremiumSearch extends HTMLElement {
    constructor() {
      super();
      this.input = null;
      this.results = null;
      this.recentEl = null;
      this.clearBtn = null;
      this.viewAll = null;
      this.debounceTimer = null;
      this.lastFetched = '';
      this.abortController = null;
      this.lastFocused = null;
    }

    connectedCallback() {
      this.input = this.querySelector('[data-sp-input]');
      this.results = this.querySelector('[data-sp-results]');
      this.recentEl = this.querySelector('[data-sp-recent]');
      this.clearBtn = this.querySelector('[data-sp-clear]');
      this.viewAll = this.querySelector('[data-sp-view-all]');

      if (!this.input || !this.results) return;

      this.bind();
      this.renderRecent();
    }

    bind() {
      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') this.onSubmit();
        if (e.key === 'Escape') this.close();
      });

      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', () => {
          this.input.value = '';
          this.onInput();
          this.input.focus();
        });
      }

      // Trending / suggested chip click → run query
      this.addEventListener('click', (e) => {
        const chip = e.target.closest('[data-sp-chip]');
        if (chip) {
          e.preventDefault();
          const term = chip.getAttribute('data-sp-chip');
          this.input.value = term;
          this.onInput();
          this.input.focus();
          return;
        }
        const remove = e.target.closest('[data-sp-recent-remove]');
        if (remove) {
          e.preventDefault();
          e.stopPropagation();
          const term = remove.getAttribute('data-sp-recent-remove');
          this.removeRecent(term);
          return;
        }
        // Save term to recent when user clicks a product/collection result
        const result = e.target.closest('[data-sp-result]');
        if (result) {
          const q = this.input.value.trim();
          if (q) this.saveRecent(q);
        }
      });

      // Open/close triggers anywhere in document
      document.querySelectorAll('[data-action="open-search"]').forEach((btn) =>
        btn.addEventListener('click', (e) => { e.preventDefault(); this.open(); })
      );
      document.querySelectorAll('[data-action="close-search"]').forEach((btn) =>
        btn.addEventListener('click', (e) => { e.preventDefault(); this.close(); })
      );

      const closest = this.closest('.search-premium');
      if (closest) {
        const backdrop = closest.querySelector('.search-premium__backdrop');
        if (backdrop) backdrop.addEventListener('click', () => this.close());
      }
    }

    /* ---------- Open / close ---------- */
    open() {
      const overlay = this.closest('.search-premium');
      if (!overlay) return;
      this.lastFocused = document.activeElement;
      overlay.classList.add('is-open');
      overlay.setAttribute('aria-hidden', 'false');
      document.body.classList.add('sp-no-scroll', 'sp-search-open');
      setTimeout(() => this.input && this.input.focus(), 80);
    }

    close() {
      const overlay = this.closest('.search-premium');
      if (!overlay) return;
      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('sp-no-scroll', 'sp-search-open');
      if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
        this.lastFocused.focus();
      }
    }

    /* ---------- Input handling ---------- */
    onInput() {
      const overlay = this.closest('.search-premium');
      const q = this.input.value.trim();
      if (overlay) overlay.classList.toggle('has-query', q.length > 0);
      if (this.viewAll) {
        const base = (window.routes && window.routes.search_url) || '/search';
        this.viewAll.href = q ? `${base}?q=${encodeURIComponent(q)}` : base;
      }

      if (q.length < MIN_QUERY) {
        clearTimeout(this.debounceTimer);
        this.renderEmpty();
        return;
      }

      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.fetch(q), DEBOUNCE_MS);
    }

    onSubmit() {
      const q = this.input.value.trim();
      if (!q) return;
      this.saveRecent(q);
      const base = (window.routes && window.routes.search_url) || '/search';
      window.location.href = `${base}?q=${encodeURIComponent(q)}`;
    }

    /* ---------- Fetch ---------- */
    async fetch(q) {
      if (q === this.lastFetched) return;
      this.lastFetched = q;

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      this.renderLoading();

      try {
        const base = (window.routes && window.routes.predictive_search_url) || '/search/suggest';
        const params = new URLSearchParams({
          q,
          section_id: SECTION_ID,
          'resources[type]': 'product,collection,page,article',
          'resources[limit]': '8',
          'resources[options][unavailable_products]': 'last',
          'resources[options][fields]': 'title,product_type,variants.title,vendor,tag,body'
        });
        const url = `${base}?${params.toString()}`;
        const res = await fetch(url, { signal: this.abortController.signal });
        if (!res.ok) throw new Error('Search request failed');
        const html = await res.text();
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        const fresh = tmp.querySelector('[data-sp-results-inner]');
        if (fresh) {
          this.results.innerHTML = fresh.innerHTML;
        } else {
          this.renderNoResults(q);
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        this.renderNoResults(q);
      }
    }

    /* ---------- Render states ---------- */
    renderEmpty() {
      this.results.innerHTML = '';
    }

    renderLoading() {
      const skel = Array.from({ length: 4 }).map(() => `
        <li class="search-premium__skeleton">
          <span class="search-premium__skeleton-media"></span>
          <span class="search-premium__skeleton-lines">
            <span class="search-premium__skeleton-line"></span>
            <span class="search-premium__skeleton-line search-premium__skeleton-line--short"></span>
          </span>
        </li>`).join('');
      this.results.innerHTML = `<ul class="search-premium__product-list search-premium__product-list--loading" role="list" aria-busy="true">${skel}</ul>`;
    }

    renderNoResults(q) {
      const chips = POPULAR.map((t) => `<li><span class="search-premium__chip" data-sp-chip="${this.escape(t)}" role="button" tabindex="0">${this.escape(t)}</span></li>`).join('');
      this.results.innerHTML = `
        <div class="search-premium__state search-premium__state--empty">
          <span class="search-premium__state-title">No fragrances found</span>
          <span class="search-premium__state-text">Nothing matched “${this.escape(q)}”. Try a scent, collection, or note.</span>
          <div class="search-premium__popular">
            <span class="search-premium__popular-label">Popular searches</span>
            <ul class="search-premium__popular-list" role="list">${chips}</ul>
          </div>
        </div>`;
    }

    escape(s) {
      const d = document.createElement('div');
      d.textContent = s;
      return d.innerHTML;
    }

    /* ---------- Recent searches ---------- */
    getRecent() {
      try {
        const raw = localStorage.getItem(RECENT_KEY);
        return raw ? JSON.parse(raw) : [];
      } catch { return []; }
    }

    saveRecent(term) {
      term = term.trim();
      if (!term) return;
      let list = this.getRecent().filter((t) => t.toLowerCase() !== term.toLowerCase());
      list.unshift(term);
      list = list.slice(0, RECENT_MAX);
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(list)); } catch {}
      this.renderRecent();
    }

    removeRecent(term) {
      const list = this.getRecent().filter((t) => t !== term);
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(list)); } catch {}
      this.renderRecent();
    }

    renderRecent() {
      if (!this.recentEl) return;
      const list = this.getRecent();
      if (!list.length) {
        this.recentEl.hidden = true;
        return;
      }
      this.recentEl.hidden = false;
      const chips = this.recentEl.querySelector('[data-sp-recent-chips]');
      if (!chips) return;
      chips.innerHTML = list.map((term) => {
        const safe = this.escape(term);
        return `
          <li>
            <span class="search-premium__chip search-premium__chip--recent" data-sp-chip="${safe}" role="button" tabindex="0">
              ${safe}
              <button type="button" class="search-premium__chip-remove" data-sp-recent-remove="${safe}" aria-label="Remove ${safe}">×</button>
            </span>
          </li>`;
      }).join('');
    }
  }

  if (!customElements.get('premium-search')) {
    customElements.define('premium-search', PremiumSearch);
  }
})();
